import React, { useState } from 'react';
import { Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import { APPLICATION_NAME } from '../constants/AppConfig';
import { parseApiError } from '../utils/errorUtils';
import AppLogo from '../images/logo.png';
import './LoginPage.css';

type PageStatus = 'form' | 'success' | 'invalid' | 'expired';

const ResetPasswordPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; confirm?: string }>({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<PageStatus>(token ? 'form' : 'invalid');

  const validate = (): boolean => {
    const errors: { password?: string; confirm?: string } = {};
    if (!password) errors.password = t('resetPassword.passwordRequired');
    else if (password.length < 8) errors.password = t('resetPassword.passwordTooShort');
    if (!confirm) errors.confirm = t('resetPassword.confirmRequired');
    else if (password !== confirm) errors.confirm = t('resetPassword.passwordMismatch');
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      await apiService.post('/auth/reset-password', { token, newPassword: password });
      setStatus('success');
    } catch (err: unknown) {
      const parsed = parseApiError(err, 'INVALID_TOKEN');
      if (parsed.code === 'EXPIRED_TOKEN') setStatus('expired');
      else setStatus('invalid');
    } finally {
      setLoading(false);
    }
  };

  const renderStatus = () => {
    if (status === 'success') {
      return (
        <div className="rp-status-state">
          <CheckCircle2 size={48} strokeWidth={1.5} className="rp-icon-success" />
          <h2 className="rp-status-title">{t('resetPassword.successTitle')}</h2>
          <p className="rp-status-message">{t('resetPassword.successMessage')}</p>
          <button type="button" className="btn-save" onClick={() => navigate('/login')}>
            {t('forgotPassword.backToLogin')}
          </button>
        </div>
      );
    }
    if (status === 'expired') {
      return (
        <div className="rp-status-state">
          <XCircle size={48} strokeWidth={1.5} className="rp-icon-error" />
          <h2 className="rp-status-title">{t('resetPassword.expiredTitle')}</h2>
          <p className="rp-status-message">{t('resetPassword.expiredMessage')}</p>
          <button type="button" className="btn-save" onClick={() => navigate('/forgot-password')}>
            {t('resetPassword.requestNew')}
          </button>
        </div>
      );
    }
    if (status === 'invalid') {
      return (
        <div className="rp-status-state">
          <XCircle size={48} strokeWidth={1.5} className="rp-icon-error" />
          <h2 className="rp-status-title">{t('resetPassword.invalidTitle')}</h2>
          <p className="rp-status-message">{t('resetPassword.invalidMessage')}</p>
          <button type="button" className="btn-save" onClick={() => navigate('/forgot-password')}>
            {t('resetPassword.requestNew')}
          </button>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src={AppLogo} alt={APPLICATION_NAME} className="login-logo" />
            <h1 className="login-title">{t('resetPassword.title')}</h1>
            <p className="login-subtitle">{APPLICATION_NAME}</p>
          </div>

          {status !== 'form' ? (
            renderStatus()
          ) : (
            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <p className="fp-instructions">{t('resetPassword.instructions')}</p>

              {/* New password */}
              <div className="form-group">
                <label className="form-label" htmlFor="rp-password">
                  {t('resetPassword.newPassword')}
                </label>
                <div className="password-input-wrapper">
                  <input
                    id="rp-password"
                    type={showPassword ? 'text' : 'password'}
                    className={`form-control${fieldErrors.password ? ' is-invalid' : ''}`}
                    placeholder={t('resetPassword.newPasswordPlaceholder')}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setFieldErrors((p) => ({ ...p, password: undefined })); }}
                    disabled={loading}
                    autoComplete="new-password"
                    autoFocus
                    maxLength={128}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.password && <small className="form-error">{fieldErrors.password}</small>}
              </div>

              {/* Confirm password */}
              <div className="form-group">
                <label className="form-label" htmlFor="rp-confirm">
                  {t('resetPassword.confirmPassword')}
                </label>
                <div className="password-input-wrapper">
                  <input
                    id="rp-confirm"
                    type={showConfirm ? 'text' : 'password'}
                    className={`form-control${fieldErrors.confirm ? ' is-invalid' : ''}`}
                    placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                    value={confirm}
                    onChange={(e) => { setConfirm(e.target.value); setFieldErrors((p) => ({ ...p, confirm: undefined })); }}
                    disabled={loading}
                    autoComplete="new-password"
                    maxLength={128}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirm((v) => !v)}
                    tabIndex={-1}
                    aria-label={showConfirm ? t('login.hidePassword') : t('login.showPassword')}
                  >
                    {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {fieldErrors.confirm && <small className="form-error">{fieldErrors.confirm}</small>}
              </div>

              <button type="submit" className="btn-save" disabled={loading}>
                {loading ? t('resetPassword.resetting') : t('resetPassword.submit')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
